// B.14a typecheck-zero: File-local types + Props interface. See docs/plans/archive/2026-04-12-typecheck-zero.
import { useState, useRef, useEffect } from 'react';
import type { ChangeEvent, ComponentType, DragEvent, FC, KeyboardEvent, MouseEvent } from 'react';
import { CloudUpload, FileText, Image, File } from 'lucide-react';
import UndoAnimatedIconImpl from '@/components/ui/UndoAnimatedIcon';
import SaveAnimatedIconImpl from '@/components/ui/SaveAnimatedIcon';

// Boundary casts for sub-component siblings (Phase B mold).
const UndoAnimatedIcon = UndoAnimatedIconImpl as unknown as ComponentType<Record<string, unknown>>;
const SaveAnimatedIcon = SaveAnimatedIconImpl as unknown as ComponentType<Record<string, unknown>>;

// ---------------- File-local companion types ----------------

type FileEditModalMode = 'add' | 'edit';

type CategoriaOption = {
  id: string;
  nome: string;
};

type CustomFieldOption = {
  value: string;
  label: string;
};

type CustomFieldConfig = {
  key: string;
  label: string;
  type?: 'text' | 'select' | string;
  required?: boolean;
  placeholder?: string;
  options?: CustomFieldOption[];
};

type EditSaveDefault = {
  descricao: string;
  newFile: File | null;
  categoriaId: string | null;
};

type EditSaveCustom = {
  customData: Record<string, string>;
  newFile: File | null;
};

type AddSaveDefaultEntry = {
  file: File;
  descricao: string;
  categoriaId: string | null;
};

type AddSaveCustomEntry = {
  file: File;
  customData: Record<string, string>;
};

type SaveEditArg = EditSaveDefault | EditSaveCustom;
type SaveAddArg = AddSaveDefaultEntry[] | AddSaveCustomEntry[];

export interface FileEditModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  onSave?: (arg: SaveEditArg | SaveAddArg) => void;
  mode?: FileEditModalMode;
  title?: string;
  pendingFiles?: File[];
  thumbnailUrl?: string | null;
  fileName?: string;
  fileSize?: number | null;
  mimeType?: string | null;
  descricao?: string;
  categoriaId?: string;
  categorias?: CategoriaOption[];
  showCategoria?: boolean;
  categoriaRequired?: boolean;
  customFields?: CustomFieldConfig[];
  customFieldValues?: Record<string, string>;
  loading?: boolean;
  allowedExtensions?: string[];
  maxFileSize?: number;
  /** Exibe o nome do arquivo no preview (default true). False oculta nomes
   *  internos/UUID — ex.: catálogo de imagens de proposta, identificado pela
   *  descrição, não pelo nome de storage. */
  showFileName?: boolean;
}

/**
 * FileEditModal - Modal para edição ou adição de arquivos
 *
 * Modos:
 * - 'edit': Edita um arquivo existente (preview + descrição + opção substituir)
 * - 'add': Adiciona arquivos já selecionados (lista de arquivos com descrição)
 */
const FileEditModal: FC<FileEditModalProps> = ({
  isOpen,
  onClose,
  onSave,
  mode = 'edit',
  title,
  pendingFiles = [],
  thumbnailUrl,
  fileName = 'Arquivo',
  fileSize,
  mimeType,
  descricao: initialDescricao = '',
  categoriaId: initialCategoriaId = '',
  categorias = [],
  showCategoria = false,
  categoriaRequired = false,
  customFields = [],
  customFieldValues = {},
  loading = false,
  allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.xml'],
  maxFileSize = 10 * 1024 * 1024,
  showFileName = true,
}) => {
  // Estado para edit mode
  const [descricao, setDescricao] = useState<string>(initialDescricao);
  const [categoriaId, setCategoriaId] = useState<string>(initialCategoriaId);
  const [newFile, setNewFile] = useState<File | null>(null);
  const [newFilePreview, setNewFilePreview] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Estado para add mode (descrições e categorias individuais)
  const [descricaoInputs, setDescricaoInputs] = useState<Record<number, string>>({});
  const [categoriaInputs, setCategoriaInputs] = useState<Record<number, string>>({});

  // Estado para custom fields
  const [customInputs, setCustomInputs] = useState<Record<number, Record<string, string>>>({}); // add mode
  const [customEditValues, setCustomEditValues] = useState<Record<string, string>>({}); // edit mode

  // Resetar estados quando modal fecha/abre
  useEffect(() => {
    if (isOpen) {
      if (mode === 'add') {
        const initialDesc: Record<number, string> = {};
        const initialCat: Record<number, string> = {};
        pendingFiles.forEach((_, index) => {
          initialDesc[index] = '';
          initialCat[index] = '';
        });
        setDescricaoInputs(initialDesc);
        setCategoriaInputs(initialCat);
        if (customFields.length > 0) {
          const init: Record<number, Record<string, string>> = {};
          pendingFiles.forEach((_, index) => {
            const fields: Record<string, string> = {};
            customFields.forEach((f) => {
              fields[f.key] = '';
            });
            init[index] = fields;
          });
          setCustomInputs(init);
        }
      } else {
        setDescricao(initialDescricao);
        setCategoriaId(initialCategoriaId);
        if (customFields.length > 0) {
          const init: Record<string, string> = {};
          customFields.forEach((f) => {
            init[f.key] = customFieldValues[f.key] || '';
          });
          setCustomEditValues(init);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode, pendingFiles.length, initialDescricao, initialCategoriaId, customFields.length]);

  if (!isOpen) return null;

  const isAddMode = mode === 'add';
  const defaultTitle = isAddMode
    ? `Adicionar ${pendingFiles.length === 1 ? 'arquivo' : `${pendingFiles.length} arquivos`}`
    : 'Editar Arquivo';
  const finalTitle = title || defaultTitle;

  const formatFileSize = (bytes: number | null | undefined): string => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const validateFile = (file: File): string | null => {
    if (file.size > maxFileSize) {
      return `Arquivo muito grande. Máximo: ${formatFileSize(maxFileSize)}`;
    }
    const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
    if (!allowedExtensions.includes(ext)) {
      return `Tipo não permitido. Permitidos: ${allowedExtensions.join(', ')}`;
    }
    return null;
  };

  // ========== Edit Mode Handlers ==========
  const handleFileSelect = (file: File): void => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    setNewFile(file);
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        const result = e.target?.result;
        setNewFilePreview(typeof result === 'string' ? result : null);
      };
      reader.readAsDataURL(file);
    } else {
      setNewFilePreview(null);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragOver(false);
  };

  // ========== Save Handlers ==========
  const handleSaveEdit = (): void => {
    if (!loading) {
      if (customFields.length > 0) {
        for (const field of customFields) {
          if (field.required && !customEditValues[field.key]) {
            setError(`${field.label} é obrigatório`);
            return;
          }
        }
        setError('');
        onSave?.({ customData: { ...customEditValues }, newFile });
      } else {
        if (showCategoria && categoriaRequired && !categoriaId) {
          setError('Categoria é obrigatória');
          return;
        }
        setError('');
        onSave?.({ descricao: descricao.trim(), newFile, categoriaId: categoriaId || null });
      }
    }
  };

  const handleSaveAdd = (): void => {
    if (!loading) {
      if (customFields.length > 0) {
        for (let i = 0; i < pendingFiles.length; i++) {
          for (const field of customFields) {
            if (field.required && !customInputs[i]?.[field.key]) {
              setError(`${field.label} é obrigatório`);
              return;
            }
          }
        }
        setError('');
        const filesWithData: AddSaveCustomEntry[] = pendingFiles.map((file, index) => ({
          file,
          customData: customInputs[index] || {},
        }));
        onSave?.(filesWithData);
      } else {
        if (showCategoria && categoriaRequired) {
          for (let i = 0; i < pendingFiles.length; i++) {
            if (!categoriaInputs[i]) {
              setError('Categoria é obrigatória para todos os arquivos');
              return;
            }
          }
        }
        setError('');
        const filesWithDescricao: AddSaveDefaultEntry[] = pendingFiles.map((file, index) => ({
          file,
          descricao: descricaoInputs[index]?.trim() || '',
          categoriaId: categoriaInputs[index] || null,
        }));
        onSave?.(filesWithDescricao);
      }
    }
  };

  const handleSave = isAddMode ? handleSaveAdd : handleSaveEdit;

  const handleClose = (): void => {
    setNewFile(null);
    setNewFilePreview(null);
    setError('');
    setDescricaoInputs({});
    setCategoriaInputs({});
    setCustomInputs({});
    setCustomEditValues({});
    onClose?.();
  };

  // ========== Edit Mode: computed values ==========
  const isImage = !!mimeType?.startsWith('image/');
  const isPdf = mimeType === 'application/pdf';
  const currentPreviewUrl = newFilePreview || thumbnailUrl;
  const currentIsImage = newFile ? newFile.type.startsWith('image/') : isImage;
  const currentIsPdf = newFile ? newFile.type === 'application/pdf' : isPdf;
  const currentFileName = newFile ? newFile.name : fileName;
  const currentFileSize = newFile ? newFile.size : fileSize;
  const hasExistingFile = !!thumbnailUrl || fileName !== 'Arquivo';

  // ========== Render ==========
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10002,
      }}
      onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: isAddMode ? '550px' : '420px',
          width: '90%',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }}
        onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#111827' }}>
            {finalTitle}
          </h3>
          {isAddMode && (
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--muted)' }}>
              {customFields.length > 0
                ? 'Preencha as informações do documento'
                : 'Adicione uma descrição para cada arquivo (opcional)'}
            </p>
          )}
        </div>

        {/* Erro */}
        {error && (
          <div
            style={{
              padding: '10px 12px',
              background: 'var(--color-danger-bg)',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: 'var(--danger)',
              fontSize: '13px',
              marginBottom: '16px',
            }}
          >
            {error}
          </div>
        )}

        {/* ========== ADD MODE: Lista de arquivos ========== */}
        {isAddMode ? (
          <div
            style={{
              maxHeight: '320px',
              overflowY: 'auto',
              marginBottom: '20px',
            }}
          >
            {pendingFiles.map((file, index) => {
              const ext = file.name.split('.').pop()?.toUpperCase() || 'FILE';
              const isFileImage = !!file.type?.startsWith('image/');
              const isFilePdf = file.type === 'application/pdf';
              return (
                <div
                  key={index}
                  style={{
                    padding: '12px',
                    background: '#f9fafb',
                    borderRadius: '8px',
                    marginBottom: index < pendingFiles.length - 1 ? '8px' : 0,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      marginBottom: '8px',
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '2px 8px',
                        background: isFileImage
                          ? '#ddd6fe'
                          : isFilePdf
                          ? '#fecaca'
                          : file.type?.includes('xml')
                          ? '#bbf7d0'
                          : '#e5e7eb',
                        color: isFileImage
                          ? '#7c3aed'
                          : isFilePdf
                          ? '#dc2626'
                          : file.type?.includes('xml')
                          ? '#16a34a'
                          : '#6b7280',
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        flexShrink: 0,
                      }}
                    >
                      {ext}
                    </span>
                    <span
                      style={{
                        fontSize: '13px',
                        color: '#374151',
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontWeight: 500,
                      }}
                      title={file.name}
                    >
                      {file.name}
                    </span>
                    <span style={{ fontSize: '11px', color: 'var(--muted)', flexShrink: 0 }}>
                      {formatFileSize(file.size)}
                    </span>
                  </div>
                  {customFields.length > 0 ? (
                    customFields.map((field, fi) => (
                      <div key={field.key} style={{ marginTop: fi > 0 ? '6px' : 0 }}>
                        <label
                          style={{
                            fontSize: '11px',
                            fontWeight: 600,
                            color: '#374151',
                            display: 'block',
                            marginBottom: '3px',
                          }}
                        >
                          {field.label}
                          {field.required ? ' *' : ''}
                        </label>
                        {field.type === 'select' ? (
                          <select
                            value={customInputs[index]?.[field.key] || ''}
                            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                              setCustomInputs((prev) => ({
                                ...prev,
                                [index]: { ...(prev[index] || {}), [field.key]: e.target.value },
                              }))
                            }
                            disabled={loading}
                            style={{
                              width: '100%',
                              padding: '7px 10px',
                              border: '1px solid var(--panel-border)',
                              borderRadius: '6px',
                              fontSize: '13px',
                              outline: 'none',
                              background: 'white',
                              boxSizing: 'border-box',
                              cursor: 'pointer',
                            }}
                          >
                            <option value="">Selecione...</option>
                            {(field.options || []).map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="text"
                            value={customInputs[index]?.[field.key] || ''}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                              setCustomInputs((prev) => ({
                                ...prev,
                                [index]: { ...(prev[index] || {}), [field.key]: e.target.value },
                              }))
                            }
                            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                              if (e.key === 'Escape') handleClose();
                            }}
                            placeholder={field.placeholder || ''}
                            disabled={loading}
                            style={{
                              width: '100%',
                              padding: '7px 10px',
                              border: '1px solid var(--panel-border)',
                              borderRadius: '6px',
                              fontSize: '13px',
                              outline: 'none',
                              background: 'white',
                              boxSizing: 'border-box',
                              transition: 'border-color 0.15s ease',
                            }}
                            autoFocus={index === 0 && fi === 0}
                          />
                        )}
                      </div>
                    ))
                  ) : (
                    <>
                      <input
                        type="text"
                        value={descricaoInputs[index] || ''}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          setDescricaoInputs((prev) => ({
                            ...prev,
                            [index]: e.target.value,
                          }))
                        }
                        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                          if (e.key === 'Escape') handleClose();
                        }}
                        placeholder="Descrição (opcional)"
                        disabled={loading}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          border: '1px solid var(--panel-border)',
                          borderRadius: '6px',
                          fontSize: '13px',
                          outline: 'none',
                          background: 'white',
                          boxSizing: 'border-box',
                          transition: 'border-color 0.15s ease',
                        }}
                        autoFocus={index === 0}
                      />
                      {showCategoria && categorias.length > 0 && (
                        <select
                          value={categoriaInputs[index] || ''}
                          onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                            setCategoriaInputs((prev) => ({
                              ...prev,
                              [index]: e.target.value,
                            }))
                          }
                          disabled={loading}
                          required={categoriaRequired}
                          style={{
                            width: '100%',
                            padding: '8px 10px',
                            border: `1px solid ${
                              categoriaRequired && error && !categoriaInputs[index]
                                ? '#ef4444'
                                : 'var(--panel-border)'
                            }`,
                            borderRadius: '6px',
                            fontSize: '13px',
                            outline: 'none',
                            background: 'white',
                            boxSizing: 'border-box',
                            marginTop: '8px',
                            cursor: 'pointer',
                          }}
                        >
                          <option value="">
                            {categoriaRequired ? 'Categoria (obrigatório)' : 'Selecione uma categoria...'}
                          </option>
                          {categorias.map((cat) => (
                            <option key={cat.id} value={cat.id}>
                              {cat.nome}
                            </option>
                          ))}
                        </select>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          /* ========== EDIT MODE: Preview + Descrição ========== */
          <>
            <div
              style={{
                position: 'relative',
                background: '#f9fafb',
                borderRadius: '8px',
                overflow: 'hidden',
                marginBottom: '16px',
                border: '1px solid var(--panel-border)',
              }}
            >
              <div
                style={{
                  position: 'relative',
                  minHeight: '180px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '20px',
                }}
                className="file-edit-preview"
              >
                {currentIsImage && currentPreviewUrl ? (
                  <img
                    src={currentPreviewUrl}
                    alt={currentFileName}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '160px',
                      objectFit: 'contain',
                      borderRadius: '4px',
                    }}
                  />
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '12px',
                      color: currentIsPdf ? 'var(--danger)' : '#6b7280',
                    }}
                  >
                    {currentIsPdf ? (
                      <FileText size={48} />
                    ) : currentIsImage ? (
                      <Image size={48} />
                    ) : (
                      <File size={48} />
                    )}
                    {showFileName && (
                      <span
                        style={{
                          fontSize: '13px',
                          fontWeight: 500,
                          color: '#374151',
                          textAlign: 'center',
                          maxWidth: '280px',
                          wordBreak: 'break-word',
                        }}
                      >
                        {currentFileName}
                      </span>
                    )}
                    {currentFileSize ? (
                      <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                        {formatFileSize(currentFileSize)}
                      </span>
                    ) : null}
                  </div>
                )}

                {(hasExistingFile || newFile) && (
                  <div
                    className="file-edit-overlay"
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'rgba(0, 0, 0, 0.6)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      opacity: 0,
                      transition: 'opacity 0.2s ease',
                      cursor: 'pointer',
                    }}
                  >
                    <CloudUpload size={32} style={{ color: 'white' }} />
                    <span style={{ color: 'white', fontSize: '13px', fontWeight: 500 }}>
                      Substituir arquivo
                    </span>
                  </div>
                )}
              </div>

              {showFileName && currentIsImage && currentPreviewUrl && (
                <div
                  style={{
                    padding: '10px 12px',
                    borderTop: '1px solid var(--panel-border)',
                    background: 'white',
                  }}
                >
                  <div
                    style={{
                      fontSize: '12px',
                      color: '#374151',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    title={currentFileName}
                  >
                    {currentFileName}
                  </div>
                  {currentFileSize ? (
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                      {formatFileSize(currentFileSize)}
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            {customFields.length > 0 ? (
              customFields.map((field, fi) => (
                <div key={field.key} style={{ marginBottom: '16px' }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#374151',
                      marginBottom: '6px',
                    }}
                  >
                    {field.label}
                    {field.required ? ' *' : ''}
                  </label>
                  {field.type === 'select' ? (
                    <select
                      value={customEditValues[field.key] || ''}
                      onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                        setCustomEditValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                      }
                      disabled={loading}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid var(--panel-border)',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontFamily: 'inherit',
                        boxSizing: 'border-box',
                        outline: 'none',
                        background: 'white',
                        cursor: 'pointer',
                      }}
                      autoFocus={fi === 0}
                    >
                      <option value="">Selecione...</option>
                      {(field.options || []).map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={customEditValues[field.key] || ''}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setCustomEditValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                      }
                      onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === 'Escape') handleClose();
                        if (e.key === 'Enter') handleSave();
                      }}
                      placeholder={field.placeholder || ''}
                      disabled={loading}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid var(--panel-border)',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontFamily: 'inherit',
                        boxSizing: 'border-box',
                        outline: 'none',
                        transition: 'border-color 0.15s ease',
                      }}
                      autoFocus={fi === 0}
                    />
                  )}
                </div>
              ))
            ) : (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#374151',
                      marginBottom: '6px',
                    }}
                  >
                    Descrição
                  </label>
                  <input
                    type="text"
                    value={descricao}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setDescricao(e.target.value)}
                    onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Escape') handleClose();
                      if (e.key === 'Enter') handleSave();
                    }}
                    placeholder="Digite uma descrição..."
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid var(--panel-border)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      boxSizing: 'border-box',
                      outline: 'none',
                      transition: 'border-color 0.15s ease',
                    }}
                    autoFocus
                  />
                </div>
                {showCategoria && categorias.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '13px',
                        fontWeight: 500,
                        color: '#374151',
                        marginBottom: '6px',
                      }}
                    >
                      Categoria{categoriaRequired && <span style={{ color: '#ef4444' }}> *</span>}
                    </label>
                    <select
                      value={categoriaId}
                      onChange={(e: ChangeEvent<HTMLSelectElement>) => setCategoriaId(e.target.value)}
                      disabled={loading}
                      required={categoriaRequired}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: `1px solid ${
                          categoriaRequired && error && !categoriaId ? '#ef4444' : 'var(--panel-border)'
                        }`,
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontFamily: 'inherit',
                        boxSizing: 'border-box',
                        outline: 'none',
                        background: 'white',
                        cursor: 'pointer',
                      }}
                    >
                      <option value="">
                        {categoriaRequired ? 'Categoria (obrigatório)' : 'Selecione uma categoria...'}
                      </option>
                      {categorias.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </>
            )}

            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: `1px dashed ${dragOver ? 'var(--color-info)' : '#d1d5db'}`,
                borderRadius: '8px',
                padding: '14px',
                cursor: 'pointer',
                background: dragOver ? 'var(--color-primary-light)' : '#f9fafb',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                marginBottom: '20px',
              }}
            >
              <CloudUpload size={18} style={{ color: '#9ca3af', flexShrink: 0 }} />
              <span style={{ fontSize: '13px', color: '#6b7280' }}>
                Arraste um arquivo aqui para substituir
              </span>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept={allowedExtensions.join(',')}
              onChange={handleInputChange}
              style={{ display: 'none' }}
            />
          </>
        )}

        {/* Footer */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button className="btn-add undo-animated" onClick={handleClose} disabled={loading}>
            <UndoAnimatedIcon size={15} />
            <span className="btn-text">Cancelar</span>
          </button>
          <button className="btn-add save-animated" onClick={handleSave} disabled={loading}>
            <SaveAnimatedIcon size={16} />
            <span className="btn-text">
              {loading
                ? isAddMode
                  ? 'Adicionando...'
                  : 'Salvando...'
                : isAddMode
                ? 'Adicionar'
                : 'Salvar'}
            </span>
          </button>
        </div>
      </div>

      <style>{`
        .file-edit-preview:hover .file-edit-overlay {
          opacity: 1 !important;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default FileEditModal;
